import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { matchPath, useLocation } from "react-router-dom";
import { IBreadcrumbItem } from "dash-ui";

import { getCurrentTabFromSearchParam } from "common/helpers";
import { RoutesPath } from "flow-components/routes/routes";
import { isRouteActive } from "shared/config/routes";
import { ApiConsoleTab, isApiConsoleTab } from "shared/config/api-console-tabs";
import {
  useGetApiCredentialsByIdLazyQuery,
  useGetUserByApiCredentialsIdLazyQuery,
} from "gql/generated.graphql";

import {
  IBreadcrumbScopeBuildParams,
  useOrganizationsBreadcrumbBuilder,
  usePropertiesBreadcrumbBuilder,
} from "./breadcrumbs-scope-builders";
import { useSaleTeamBreadcrumbBuilder } from "./breadcrumbs-scope-builders/use-sale-team-breadcrumb-builder";
import { useApiBreadcrumbBuilder } from "./breadcrumbs-scope-builders/use-api-breadcrumb-builder";
import { useFeaturedPortfoliosBreadcrumbBuilder } from "./breadcrumbs-scope-builders/use-featured-portfolios-breadcrumb-builder";

export const useBreadcrumbBuilder = (): [
  IBreadcrumbItem[],
  (breadcrumbItems: IBreadcrumbItem[]) => void
] => {
  const [getAsyncPropertiesBreadcrumbItems] = usePropertiesBreadcrumbBuilder();
  const [getAsyncOrganizationsBreadcrumbItems] =
    useOrganizationsBreadcrumbBuilder();
  const [getAsyncSaleTeamBreadcrumbItems] = useSaleTeamBreadcrumbBuilder();
  const [getAsyncApiBreadcrumbItems] = useApiBreadcrumbBuilder();
  const [getAsyncFeaturedPortfoliosBreadcrumbItems] =
    useFeaturedPortfoliosBreadcrumbBuilder();
  // Console entity crumbs: called unconditionally per rules-of-hooks, fired
  // only inside the /api-console/accounts|plugin-users/:id branch below.
  const [getApiCredentialsById] = useGetApiCredentialsByIdLazyQuery();
  const [getUserByApiCredentialsId] = useGetUserByApiCredentialsIdLazyQuery();

  const [breadcrumbItems, setBreadcrumbItems] = useState<IBreadcrumbItem[]>([]);

  const { pathname, search } = useLocation();
  const { t } = useTranslation("common", { keyPrefix: "labels" });
  // Tab labels come from the same pages:apiConsolePage.tabs keys the console
  // page's Tabs render, so the crumb and the tab can never disagree.
  const { t: tPages } = useTranslation("pages");
  const currentTab = getCurrentTabFromSearchParam(search);

  // FIXME: re-creating context functions everytime state change. Use useCallback
  useEffect(() => {
    const fetchItems = async () => {
      const buildParams: IBreadcrumbScopeBuildParams = {
        t,
        pathname,
        currentTab,
      };

      let items: IBreadcrumbItem[] = [];

      if (pathname.includes(RoutesPath.Properties)) {
        items = await getAsyncPropertiesBreadcrumbItems(buildParams);
      }

      if (pathname.includes(RoutesPath.Organizations)) {
        items = await getAsyncOrganizationsBreadcrumbItems(buildParams);
      }

      if (pathname.includes(RoutesPath.ExternalSales)) {
        items = await getAsyncSaleTeamBreadcrumbItems(buildParams);
      }

      if (pathname.includes(RoutesPath.MarketAreas)) {
        items = [
          {
            label: t`marketAreas`,
            path: RoutesPath.MarketAreas,
            icon: "mapMarker",
          },
        ];
      }

      if (pathname.includes(RoutesPath.Amenities)) {
        items = [
          {
            label: t`amenities`,
            path: RoutesPath.Amenities,
            icon: "artboard",
          },
        ];
      }

      if (pathname.includes(RoutesPath.FeaturedContent)) {
        items = await getAsyncFeaturedPortfoliosBreadcrumbItems(buildParams);
      }

      if (pathname.includes(RoutesPath.Brokers)) {
        items = [
          {
            label: t`brokers`,
            path: RoutesPath.Brokers,
            icon: "checklist",
          },
        ];
      }

      if (pathname.includes(RoutesPath.Seekers)) {
        items = [
          {
            label: t`seekers`,
            path: RoutesPath.Seekers,
            icon: "checklist",
          },
        ];
      }

      if (pathname.includes(RoutesPath.Management)) {
        items = [
          {
            label: t`management`,
            path: RoutesPath.Management,
            icon: "setting2",
          },
        ];
      }

      // Checked before the legacy branch and by prefix, not substring: "/api"
      // is contained in "/api-console", so the legacy builder would otherwise
      // claim the console and label its tabs with keys it does not have.
      if (isRouteActive(pathname, RoutesPath.APIConsole)) {
        items = [
          { label: t`apiConsole`, path: RoutesPath.APIConsole, icon: "world" },
        ];

        const accountsTabCrumb: IBreadcrumbItem = {
          label: tPages(`apiConsolePage.tabs.${ApiConsoleTab.Accounts}`),
          path: `${RoutesPath.APIConsole}?tab=${ApiConsoleTab.Accounts}`,
        };

        const accountMatch = matchPath(
          RoutesPath.APIConsoleAccountDetails,
          pathname
        );
        const pluginUserMatch = matchPath(
          RoutesPath.APIConsolePluginUserDetails,
          pathname
        );

        if (accountMatch?.params.id) {
          // Awaited before setBreadcrumbItems is ever called below, so the
          // previous crumb trail stays on screen instead of flashing the id.
          const { data } = await getApiCredentialsById({
            variables: { apiCredentialsId: accountMatch.params.id },
          });

          items.push(accountsTabCrumb, {
            label: data?.getApiCredentialsById?.companyName ?? "",
            path: pathname,
          });
        } else if (pluginUserMatch?.params.id) {
          const { data } = await getUserByApiCredentialsId({
            variables: {
              filter: { apiCredentialsId: pluginUserMatch.params.id },
            },
          });

          const firstName = data?.getUserByApiCredentialsId?.profile?.firstName;
          const lastName = data?.getUserByApiCredentialsId?.profile?.lastName;
          const fullName = lastName
            ? `${firstName} ${lastName}`
            : firstName || "";

          items.push(accountsTabCrumb, { label: fullName, path: pathname });
        } else if (
          isApiConsoleTab(currentTab) &&
          currentTab !== ApiConsoleTab.Overview
        ) {
          items.push({
            label: tPages(`apiConsolePage.tabs.${currentTab}`),
            path: `${RoutesPath.APIConsole}?tab=${currentTab}`,
          });
        }
      } else if (pathname.includes(RoutesPath.APIManagement)) {
        items = await getAsyncApiBreadcrumbItems(buildParams);
      }

      if (pathname.includes(RoutesPath.ContentActivity)) {
        items = [
          {
            label: t`contentActivity`,
            path: RoutesPath.ContentActivity,
            icon: "history",
          },
        ];
      }

      if (pathname.includes(RoutesPath.Account)) {
        items = [{ label: t`account` }];
      }

      setBreadcrumbItems(items);
    };

    fetchItems().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, t, tPages, currentTab]);

  return [breadcrumbItems, (items) => setBreadcrumbItems(items)];
};
